// src/pages/ContentDetailPage.js

import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import ArticleBody from '../components/ArticleBody';
import { ARTICLE_POPULATE } from '../utils/articleQuery';

import { ReactComponent as ChevronUpIcon } from '../assets/icon-chevron-up.svg';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

function ContentDetailPage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const pathSegment = location.pathname.split('/')[1];

    let apiEndpoint;
    let populateQuery;

    switch (pathSegment) {
      case 'nieuws-item':
        apiEndpoint = 'nieuws-items';
        // Populate alle gerelateerde data, inclusief doorloopjes & digidates
        populateQuery =
          'populate=featured_image&populate=auteurs.photo' +
          '&populate=related_doorloopjes.steps.image&populate=related_digidates.image';
        break;
      case 'artikel':
      case 'artikelen':
        apiEndpoint = 'artikels';
        populateQuery = ARTICLE_POPULATE;
        break;
      default:
        setError('Onbekend content type');
        setLoading(false);
        return;
    }

    const jwt = localStorage.getItem('jwt');
    const fetchContent = async () => {
      setLoading(true);
      setError('');
      try {
        const url = `${STRAPI_URL}/api/${apiEndpoint}?filters[slug][$eq]=${slug}&${populateQuery}`;
        const response = await axios.get(url, { headers: { Authorization: `Bearer ${jwt}` } });

        if (response.data.data.length > 0) {
          setContent(response.data.data[0]);
        } else {
          setError('Deze pagina bestaat niet of is verplaatst.');
        }
      } catch (err) {
        console.error('API Fout:', err.response?.data || err.message);
        setError('Deze pagina laden lukte niet. Probeer het opnieuw.');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [slug, location.pathname]);

  if (loading) return <p>Pagina wordt geladen...</p>;
  if (error || !content) return <p>{error || 'Deze pagina bestaat niet of is verplaatst.'}</p>;

  const imageUrl = content.featured_image?.url || null;
  const rawSegment = location.pathname.split('/')[1];
  const breadcrumbText = (rawSegment === 'artikelen' ? 'artikel' : rawSegment).replace(/-/g, ' ');
  const isNewsDetail = location.pathname.startsWith('/nieuws-item');
  const isArticle = location.pathname.startsWith('/artikel');

  return (
    <div className="detail-page-container article-view">
      <button className="back-button" onClick={() => navigate(-1)}>
        <ChevronUpIcon className="back-icon" />
        <span>Terug</span>
      </button>
      <main className="detail-main-content">
        <p className="detail-breadcrumb">{breadcrumbText}</p>
        <ArticleBody
          article={content}
          beforeBody={!isArticle && imageUrl && (
            <img src={imageUrl} alt={content.title} className="article-featured-image" />
          )}
        />
      </main>

      {/* ===== Gerelateerde Doorloopjes ===== */}
      {isNewsDetail && Array.isArray(content.related_doorloopjes) && content.related_doorloopjes.length > 0 && (
        <div className="detail-main-content">
          <h3 className="related-title">Gerelateerde Doorloopjes</h3>
          <div className="related-doorloopjes-list">
            {content.related_doorloopjes.map(d => {
              const img = d?.steps?.[0]?.image?.formats?.thumbnail?.url || d?.steps?.[0]?.image?.url;
              return (
                <RouterLink to={`/doorloopjes/${d.slug}`} key={d.id} className="doorloopje-card">
                  <div className="card-icon">{img ? <img src={img} alt={d.title} /> : <div className="placeholder" />}</div>
                  <div className="card-text"><h3>{d.title}</h3></div>
                  <div className="card-arrow">{'>'}</div>
                </RouterLink>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== Gerelateerde Webinars (Digidates) ===== */}
      {isNewsDetail && Array.isArray(content.related_digidates) && content.related_digidates.length > 0 && (
        <div className="detail-main-content">
          <h3 className="related-title">Gerelateerde Webinars</h3>
          <div className="related-doorloopjes-list">
            {content.related_digidates.map(w => {
              const img = w?.image?.formats?.thumbnail?.url || w?.image?.url;
              const dateStr = w?.event_date
                ? new Date(w.event_date).toLocaleDateString('nl-NL', {
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                  })
                : null;
              return (
                <RouterLink to={`/webinars/${w.slug}`} key={w.id} className="doorloopje-card">
                  <div className="card-icon">{img ? <img src={img} alt={w.title} /> : <div className="placeholder" />}</div>
                  <div className="card-text">
                    <h3>{w.title}</h3>
                    {dateStr && (
                      <p>{w.has_taken_place ? 'Opname van' : 'Aankomend op'} {dateStr}</p>
                    )}
                    {!w.has_taken_place && (
                      <p className="signup-prompt"><strong>Meld je aan</strong></p>
                    )}
                  </div>
                  <div className="card-arrow">{'>'}</div>
                </RouterLink>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ContentDetailPage;
